<h2 mat-dialog-title>Notificaciones de despliegues</h2>

<mat-dialog-content class="dialog-content">
  @if (!showForm) {
    <p class="dialog-description">
      Destinatarios de avisos al completar un despliegue. El canal interno se notifica siempre en producción (éxito y fallo).
    </p>

    <section class="section-block">
      <div class="section-header">
        <h3>Automáticos</h3>
      </div>

      @if (internal_team_conversation; as conversation) {
        <ul class="defaults-list">
          <li>
            <mat-icon aria-hidden="true">forum</mat-icon>
            <div class="item-main">
              <span class="item-title">{{ conversation.display_name }}</span>
              <span class="item-meta">Producción · Éxito · Fallo</span>
            </div>
            <span class="reason-chip">Canal interno</span>
          </li>
        </ul>
      } @else {
        <p class="empty-inline">No hay canal interno configurado en el producto.</p>
      }
    </section>

    <section class="section-block">
      <div class="section-header">
        <h3>Adicionales</h3>
        <button mat-stroked-button type="button" color="primary" (click)="showCreateForm()">
          <mat-icon>add</mat-icon>
          Añadir
        </button>
      </div>

      @if (additional_configurations.length === 0) {
        <p class="empty-inline">No hay destinatarios adicionales configurados.</p>
      } @else {
        <ul class="config-cards">
          @for (config of additional_configurations; track config.id) {
            <li class="config-card">
              <div class="config-card-body">
                <div class="config-card-title">{{ getNotifiableName(config) }}</div>
                <div class="config-card-meta">
                  <span class="meta-chip">{{ getEnvironmentName(config) }}</span>
                  <span class="meta-chip">{{ getNotifyEventsLabel(config) }}</span>
                  <span class="meta-chip">{{ getChannelsDisplay(config) }}</span>
                </div>
              </div>
              <div class="config-card-actions">
                <button mat-icon-button type="button" (click)="showEditForm(config)" aria-label="Editar">
                  <mat-icon>edit</mat-icon>
                </button>
                <button mat-icon-button type="button" (click)="deleteConfiguration(config)" aria-label="Eliminar">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            </li>
          }
        </ul>
      }
    </section>
  } @else {
    <form [formGroup]="form" class="form-content">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Entorno</mat-label>
        <mat-select formControlName="role">
          <mat-option value="production">Producción</mat-option>
          <mat-option value="staging">Staging</mat-option>
          <mat-option value="development">Desarrollo</mat-option>
          <mat-option value="other">Otro</mat-option>
        </mat-select>
        @if (form.get('role')?.hasError('required') && form.get('role')?.touched) {
          <mat-error>El entorno es obligatorio</mat-error>
        }
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Tipo de destinatario</mat-label>
        <mat-select formControlName="notifiable_type">
          <mat-option value="App\Models\User">Usuario (Mail / Slack / Teams)</mat-option>
          <mat-option value="App\Models\MessagingConversation">Conversación de mensajería</mat-option>
        </mat-select>
        @if (form.get('notifiable_type')?.hasError('required') && form.get('notifiable_type')?.touched) {
          <mat-error>El tipo de destinatario es obligatorio</mat-error>
        }
      </mat-form-field>

      @if (isUserType()) {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Usuario</mat-label>
          <input type="text" matInput [formControl]="userAutocompleteControl" [matAutocomplete]="userAuto" placeholder="Buscar usuario…" />
          <mat-autocomplete #userAuto="matAutocomplete" [displayWith]="displayUserFn">
            @for (user of filteredUsers | async; track user.id) {
              <mat-option [value]="user">{{ user.full_name }}</mat-option>
            }
          </mat-autocomplete>
          @if (form.get('notifiable_id')?.hasError('required') && form.get('notifiable_id')?.touched) {
            <mat-error>El usuario es obligatorio</mat-error>
          }
        </mat-form-field>
      } @else if (isMessagingConversationType()) {
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Conversación</mat-label>
          <mat-select formControlName="notifiable_id">
            @for (conversation of availableMessagingConversations; track conversation.id) {
              <mat-option [value]="conversation.id"> {{ conversation.display_name }} ({{ conversation.provider }}) </mat-option>
            }
          </mat-select>
          @if (form.get('notifiable_id')?.hasError('required') && form.get('notifiable_id')?.touched) {
            <mat-error>La conversación es obligatoria</mat-error>
          }
        </mat-form-field>
      }

      <div class="checkbox-grid">
        <mat-checkbox formControlName="notify_on_success">Notificar en éxito</mat-checkbox>
        <mat-checkbox formControlName="notify_on_failure">Notificar en fallo</mat-checkbox>
        <mat-checkbox formControlName="only_notify_if_has_mapped_tasks">Solo si hay tareas mapeadas</mat-checkbox>
        <mat-checkbox formControlName="include_tasks_in_messages">Incluir tareas en el mensaje</mat-checkbox>
        <mat-checkbox formControlName="include_task_assignees_in_messages">Incluir asignados de tareas</mat-checkbox>
        <mat-checkbox formControlName="include_company_product_name_in_messages">Incluir nombre del producto</mat-checkbox>
      </div>

      @if (isUserType()) {
        <div class="channels-block">
          <div class="channels-label">Canales</div>
          <div class="channels-container">
            <mat-checkbox [formControl]="emailChannelControl">Email</mat-checkbox>
            <mat-checkbox [formControl]="slackChannelControl">Slack</mat-checkbox>
            <mat-checkbox [formControl]="teamsChannelControl">Microsoft Teams</mat-checkbox>
          </div>
          @if (form.get('notification_channels')?.hasError('required') && form.get('notification_channels')?.touched) {
            <mat-error>Selecciona al menos un canal</mat-error>
          }
        </div>
      }
    </form>
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  @if (showForm) {
    <button mat-button type="button" (click)="cancelForm()">Cancelar</button>
    <button mat-flat-button color="primary" type="button" [disabled]="!form.valid" (click)="submit()">
      {{ editingConfiguration ? 'Guardar' : 'Crear' }}
    </button>
  } @else {
    <button mat-flat-button color="primary" type="button" (click)="close()">Cerrar</button>
  }
</mat-dialog-actions>
